import {ListView} from '@github-ui/list-view'
import {ListViewMetadata} from '@github-ui/list-view/ListViewMetadata'
import {RepoStacksState} from '@github-ui/dotcom-schema/types/pull_requests/v1/repo_stacks'
import {useRouteParams} from '@github-ui/react-core/future/use-route-params'
import {useSuspenseRouteQuery} from '@github-ui/react-core/future/use-route-query'
import {useNavigate, useSearchParams} from '@github-ui/use-navigate'
import {testIdProps} from '@github-ui/test-id-props'
import {Pagination} from '@primer/react'
import type {MouseEvent} from 'react'

import {StackListRow} from '../components/stacks/StackListRow'
import {StacksSortMenu} from '../components/stacks/StacksSortMenu'
import {StacksStateFilters} from '../components/stacks/StacksStateFilters'
import styles from '../components/stacks/Stacks.module.css'
import {STACKS_CONTENT_TEST_ID} from '../components/stacks/test-ids'
import {repoStacksListRoute, stacksListPath} from './repo-stacks-list-route'

// Leave modified and non-primary clicks to the browser, so Ctrl/Cmd-click opens the page in a new tab.
function isModifiedClick(event: MouseEvent) {
  // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
  return event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey
}

export function RepoStacksList() {
  const {owner, repo} = useRouteParams(repoStacksListRoute)
  const {data} = useSuspenseRouteQuery(repoStacksListRoute, 'mainQuery')
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const basePath = stacksListPath({owner, repo})
  const isCompleted = data.state === RepoStacksState.COMPLETED

  const pageHref = (page: number) => {
    const params = new URLSearchParams(searchParams)
    if (page > 1) {
      params.set('page', String(page))
    } else {
      params.delete('page')
    }
    const search = params.toString()
    return search ? `${basePath}?${search}` : basePath
  }

  return (
    <div {...testIdProps(STACKS_CONTENT_TEST_ID)}>
      <h1 className={styles.title}>Stacks</h1>
      <div className={styles.listWrapper}>
        <ListView
          title={isCompleted ? 'Completed stacks' : 'Open stacks'}
          titleHeaderTag="h2"
          singularUnits="stack"
          pluralUnits="stacks"
          totalCount={isCompleted ? data.completedCount : data.openCount}
          emptyState={
            <p className={styles.blankslate}>
              {isCompleted ? 'There are no completed stacks' : 'There are no open stacks'}
            </p>
          }
          metadata={
            <ListViewMetadata
              actionsLabel="Actions"
              sectionFilters={
                <StacksStateFilters
                  basePath={basePath}
                  state={data.state}
                  openCount={data.openCount}
                  completedCount={data.completedCount}
                />
              }
              actions={[
                {key: 'stacks-sort-menu', render: () => <StacksSortMenu basePath={basePath} sort={data.sort} />},
              ]}
            />
          }
        >
          {data.stacks.map(stack => (
            <StackListRow key={String(stack.id)} stack={stack} />
          ))}
        </ListView>
      </div>
      {data.totalPages > 1 ? (
        <Pagination
          className={styles.pagination}
          pageCount={data.totalPages}
          currentPage={data.currentPage}
          hrefBuilder={pageHref}
          onPageChange={(event, page) => {
            if (event.defaultPrevented || isModifiedClick(event)) return
            event.preventDefault()
            navigate(pageHref(page))
          }}
        />
      ) : null}
    </div>
  )
}
